/**
 * Helpers for the SIGNED-OUT waitlist X join flow (signup v2) — the
 * anonymous sibling of `x-connect-result.ts`.
 *
 * Platform-neutral pure functions only. The web launcher + URL cleanup
 * live in `src/platform/web/auth/oauth/x-waitlist.ts`.
 *
 * Redirect-only flow (the early-access gate is a full page; no popup):
 * the gate navigates to the backend `/v1/waitlist/x/start`, the backend
 * bounces through x.com and lands back on `/early-access` with
 * `?waitlist_x=*` result params, which `readXWaitlistFromUrl` parses.
 */

const URL_PARAM_RESULT = 'waitlist_x';
const URL_PARAM_HANDLE = 'handle';
const URL_PARAM_X_TOKEN = 'xt';

export type XWaitlistJoinResult =
  | { kind: 'joined'; handle: string }
  | {
      kind: 'need_email';
      handle: string;
      /**
       * HMAC-signed X-identity blob minted by the backend callback. The
       * gate attaches it verbatim to `otp/start` (`xToken`) so the email
       * verify upgrades the already-stored handle-only row. Opaque to the
       * client; the backend re-verifies the signature.
       */
      xToken: string;
    }
  | { kind: 'cancelled' }
  | { kind: 'error' };

/**
 * Build the backend `/v1/waitlist/x/start` URL. `apiBaseUrl` is the
 * env-provided Morfi API origin (callers pass `getNetworkConfig().apiUrl`).
 */
export function buildXWaitlistStartUrl(
  apiBaseUrl: string,
  args: { ref?: string | null; locale?: string },
): string {
  const url = new URL('/v1/waitlist/x/start', apiBaseUrl);
  if (typeof args.ref === 'string' && args.ref.length > 0) {
    url.searchParams.set('ref', args.ref);
  }
  if (typeof args.locale === 'string' && args.locale.length > 0) {
    url.searchParams.set('locale', args.locale);
  }
  return url.toString();
}

/**
 * Inspect a URL search string and return the waitlist X join result encoded
 * by the backend callback redirect. Returns `null` when no flag is present
 * (this page load is unrelated to a waitlist X return).
 */
export function readXWaitlistFromUrl(search: string): XWaitlistJoinResult | null {
  const params = new URLSearchParams(search);
  const result = params.get(URL_PARAM_RESULT);
  if (result === null) return null;
  const handle = params.get(URL_PARAM_HANDLE) ?? '';
  if (result === 'joined') {
    return { kind: 'joined', handle };
  }
  if (result === 'need_email') {
    const xToken = params.get(URL_PARAM_X_TOKEN) ?? '';
    return { kind: 'need_email', handle, xToken };
  }
  if (result === 'cancelled') {
    return { kind: 'cancelled' };
  }
  return { kind: 'error' };
}
